/**
 * A table of nyr
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import Table, { TableRow, TableCell } from "@amzn/meridian/table";
import NYRTableRow from "./nyr-table-row";
import nyrTableColumns from "../../../data/nyr-table-columns";
import { getDisplayQuantity } from "../../../utilities/parent-child-conversion";

/**
 * Compare two numeric values for sorting
 */
const compareValues = (a, b, direction) => {
  if (a < b) {
    return direction === "ascending" ? -1 : 1;
  }
  if (a > b) {
    return direction === "ascending" ? 1 : -1;
  }
  return 0;
};

/**
 * Get converted confirmed quantity for sorting
 */
const getConfirmedQty = (item) => {
  return getDisplayQuantity(item.quantityConfirmed || 0, item.child_quantity_confirmed);
};

/**
 * Get converted received quantity for sorting
 */
const getReceivedQty = (item) => {
  return getDisplayQuantity(item.quantityReceived || 0, item.child_quantity_received);
};

/**
 * Get backlog quantity for sorting (confirmed - received)
 */
const getBacklogQty = (item) => {
  return Math.max(0, getConfirmedQty(item) - getReceivedQty(item));
};

/**
 * Create comparator function for NYR table sorting
 */
const createNYRComparator = (sortColumn, sortDirection) => {
  return (a, b) => {
    if (sortColumn === "vendorConfirmedUnits") {
      return compareValues(getConfirmedQty(a), getConfirmedQty(b), sortDirection);
    }

    if (sortColumn === "vendorReceivedUnits") {
      return compareValues(getReceivedQty(a), getReceivedQty(b), sortDirection);
    }

    if (sortColumn === "backlog") {
      return compareValues(getBacklogQty(a), getBacklogQty(b), sortDirection);
    }

    if (sortColumn === "quantityConfirmed") {
      return compareValues(a.quantityConfirmed || 0, b.quantityConfirmed || 0, sortDirection);
    }

    // Handle all other columns with standard sorting
    const aSortColumn = a[sortColumn];
    const bSortColumn = b[sortColumn];

    // Handle numbers
    if (typeof aSortColumn === 'number' && typeof bSortColumn === 'number') {
      return compareValues(aSortColumn, bSortColumn, sortDirection);
    }

    // Handle strings
    const aStr = String(aSortColumn || '');
    const bStr = String(bSortColumn || '');
    if (aStr < bStr) {
      return sortDirection === "ascending" ? -1 : 1;
    }
    if (aStr > bStr) {
      return sortDirection === "ascending" ? 1 : -1;
    }
    return 0;
  };
};

const NYRTable = props => {
  const { nyrData, criticalAsinEvents, region_id } = props;
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("ascending");

  const onSort = ({ sortColumn, sortDirection }) => {
    setSortDirection(sortDirection)
    setSortColumn(sortColumn)
  };
  // Make a copy of incoming shipments and sort if `sortColumn` exists
  const sortedNyrItems = [...nyrData];
  if (sortColumn) {
    sortedNyrItems.sort(createNYRComparator(sortColumn, sortDirection));
  }

  const visibleNyrItems = sortedNyrItems.slice(
    props.visibleIndices[0],
    props.visibleIndices[1]
  );

  return (
    <React.Fragment>
      <Table
        headerRows={1}
        showDividers={true}
        rowComponents={[NYRTableRow, TableRow]}
        stickyHeaderRow={true}
        stickyHeaderColumn={false}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={onSort}
        showStripes={true}
      >
        <TableRow>
          {nyrTableColumns
            .map(header => (
              <TableCell
                key={header.id}
                sortColumn={header.id}
                width={header.width}
                alignmentHorizontal={header.alignment}
              >
                {header.label}
              </TableCell>
            ))}
        </TableRow>
        {visibleNyrItems.map((nyrItem, index) => (
          <NYRTableRow
            key={`${nyrItem.tempZone}-${nyrItem.asin}-${index}`}
            nyrItem={nyrItem}
            criticalAsinEvents={criticalAsinEvents}
            region_id={region_id}
          />
        ))}
      </Table>
    </React.Fragment>
  );
};

NYRTable.propTypes = {
  visibleIndices: PropTypes.arrayOf(PropTypes.number).isRequired,
  nyrData: PropTypes.arrayOf(PropTypes.object).isRequired,
  criticalAsinEvents: PropTypes.object.isRequired,
  region_id: PropTypes.number.isRequired,
};

export default NYRTable;
